import React, { Fragment } from "react";
import { withStyles, Button } from "@material-ui/core";
import DataGrid from '@dataGrid';
import styles from "../../styles";
import GridConfig from "./gridConfig";
import {
  getDataWithQueryString,
  postData,
} from "../../../../../api/componentAPIs";
import { compose } from "recompose";
import { withSnackbar } from "notistack";
import { withUserProfile } from "../../../../../userProfile/userProfileContext";
import { DateTimeEditor } from '@dataGrid/components/cellEditors';
import appConstant from "../../../../../appConstants";
import MESSAGES from "../../../../../appMessages";
import { getUTCDate, formatDateYear } from '@dataGrid/gridUtilities';
import { isValid as isDateTimeValid, format } from "date-fns";
import Events from "../../../../../utilities/EventEmitter";
import CircularProgress from "@material-ui/core/CircularProgress";
import SignalRClient from "../../../../../signalR/index";
import Tooltip from "../../../../common/StyledToolTip";
import CustomConfirmationDialog from "../../../Dispatch/RouteExecution/DriverPopUp/common/CustomConfirmationDialog";
import { WARNING_MESSAGE, LABELS } from "./constants";
import { sanitizeInput } from "../../../../../utilities/dataUtils";
import { observer, inject } from "mobx-react";
import { getAnchorPointData, getTrailersData, getWavesData } from "../../../../../utilities/fetchData";
import { isDispatchHeaderActive } from "../../utils";

@inject("CommonStore")
@observer
class ScenarioExplorer extends React.Component {
  constructor(props) {
    super(props);
    this.gridConfig = new GridConfig();
    this.state = {
      scenariosData: null,
      date: this.props.userData.userProfile.planDate,
      errorMessage: null,
      createDefaultScenarioInProgress: false,
      showCreateNewScenarioWarning : false,
    };
    this.PLAN_DATE_FORMAT = "yyyy-MM-dd";
    this.refreshScenarioNameAfterDefaultCopy = false;
  }

  componentDidMount() {
    this.getScenariosList();
    this._isComponentMounted = true;
  }

  componentWillUnmount() {
    this._isComponentMounted = false;
  }

  componentDidUpdate(prevProps) {
    if (
      this.props.userData.userProfile.terminal.id !==
      prevProps.userData.userProfile.terminal.id
    ) {
      if (this.state.date === this.props.userData.userProfile.planDate) {
        this.getScenariosList();
      } else {
        this.onDateChanged(this.props.userData.userProfile.planDate);
      }
    }
  }

  getScenariosList = (date = this.state.date) => {
    const terminalId = this.props.userData.userProfile.terminal.id;
    getDataWithQueryString(
      "GetScenarios",
      `terminalId=${terminalId}&planDate=${date}`
    ).then(
      (response) => {
        if (this._isComponentMounted) {
          if (response && response.status >= 200 && response.status < 400) {
            this.setState({
              date: date,
              scenariosData: response.data,
              errorMessage: null,
            });
          } else {
            this.setState({
              date: date,
              scenariosData: null,
              errorMessage: MESSAGES.COMMON.GRID_DATA_FETCH_FAILED,
            });
            this.props.enqueueSnackbar(MESSAGES.SCENARIO.FETCH_DATA_FAILURE, {
              variant: "error",
            });
          }
        }
      },
      (error) => {
        if (this._isComponentMounted) {
          this.setState({
            date: date,
            scenariosData: null,
            errorMessage: MESSAGES.COMMON.GRID_DATA_FETCH_FAILED,
          });
          this.props.enqueueSnackbar(MESSAGES.SCENARIO.FETCH_DATA_FAILURE, {
            variant: "error",
          });
        }
      }
    );
  };

  onGridReady = ({ api }) => {
    this.gridApi = api;
  };

  onScenariosRowDataUpdated = () => {
    if (this.refreshScenarioNameAfterDefaultCopy) {
      this.gridApi?.refreshCells({
        columns: ["scenarioName"],
        force: true,
      });
      this.refreshScenarioNameAfterDefaultCopy = false;
    }
  };

  onDateChanged = (value) => {
    if (!isDateTimeValid(value)) {
      return;
    }
    const changedValue = format(getUTCDate(value), this.PLAN_DATE_FORMAT);
    if (this.state.date !== changedValue) {
      this.getScenariosList(changedValue);
    }
  };

  isUniqueScenarioName = ({ scenarioId, scenarioName }) => {
    return (
      this.state.scenariosData &&
      this.state.scenariosData.find(
        (scenario) =>
          scenario.scenarioId !== scenarioId &&
          scenario.scenarioName.toLowerCase() === scenarioName.toLowerCase()
      )
    );
  };

  onCellValueChanged = (params) => {
    if (
      params.colDef.field === "scenarioDescription" &&
      params.oldValue !== params.newValue
    ) {
      this.updateScenarioData(params);
    } else if (
      params.colDef.field === "scenarioName" &&
      (params.oldValue !== params.newValue || !params.data.scenarioId)
    ) {
      // if scenarioName changed or new scenario is being created
      if (params.newValue === "") {
        if (params.data.scenarioId) {
          params.data[params.colDef.field] = params.oldValue;
          this.props.enqueueSnackbar(
            MESSAGES.SCENARIO.UPDATE_SCENARIONAME_EMPTY,
            { variant: "warning" }
          );
          this.gridApi.refreshCells();
        } else {
          this.props.enqueueSnackbar(
            MESSAGES.SCENARIO.UPDATE_SCENARIONAME_EMPTY,
            { variant: "warning" }
          );
          this.gridApi.stopEditing(true);
          // When the new scenario name is in edit mode and we click on a row at top of grid by scrolling, the focus will move back to the editing cell
          // and onCellClicked will not be fired , to make sure onCellClicked is fired we are adding a settimeout
          // setTimeout(()=>{
          //     if(this.gridApi.rowModel.rowsToDisplay.length > params.node.rowIndex){
          //         this.gridApi.startEditingCell({
          //             rowIndex: params.node.rowIndex,
          //             colKey: params.colDef.field
          //         });
          //     }
          // },100)
        }
      } else if (
        this.isUniqueScenarioName({
          scenarioId: params.data.scenarioId,
          scenarioName: params.newValue,
        })
      ) {
        this.props.enqueueSnackbar(
          `Scenario with name ${params.newValue} already exists`,
          { variant: "error" }
        );
        if (params.data.scenarioId) {
          params.data[params.colDef.field] = params.oldValue;
          this.gridApi.refreshCells();
        }
        this.gridApi.stopEditing(true);
        this.gridApi.startEditingCell({
          rowIndex: params.node.rowIndex,
          colKey: params.colDef.field,
        });
      } else {
        if (this.currentScenarioId) {
          this.copyScenarioData(params);
          this.gridApi.stopEditing(true);
        } else {
          this.updateScenarioData(params);
        }
      }
    } else if (params.colDef.field === "isActive") {
      this.updateActiveScenario(params);
    }
  };

  updateActiveScenario = (params) => {
    this.updateScenarioActiveStatus(params.data.scenarioId);
    postData("SetUserPreferences", {
      activeScenarioId: params.data.scenarioId,
      terminalId: this.props.userData.userProfile.terminal.id,
    }).then(
      (response) => {
        if (response && Math.trunc(response.status / 100) === 2) {
          this.props.enqueueSnackbar(
            `Active scenario: ${params.data.scenarioName} `,
            { variant: "success" }
          );
          this.props.userData.updateUserProfile({
            scenarioID: params.data.scenarioId,
            planDate: format(
              getUTCDate(this.state.date),
              this.PLAN_DATE_FORMAT
            ),
            scenarioName: params.data.scenarioName,
            isDefaultScenario: params.data.isDefault,
          });
          this.updateDataInStore();
          SignalRClient.disconnectFromHub(params.data.scenarioId).then(() => {
            SignalRClient.connectToHub(
              params.data.scenarioId,
              this.props.userData.userProfile.terminal.code
            );
          });
          return;
        }
        this.handleUserPreferenceError();
      },
      () => {
        this.handleUserPreferenceError();
      }
    );
  };

  handleUserPreferenceError = () => {
    this.props.enqueueSnackbar(
      MESSAGES.SCENARIO.UPDATE_ACTIVESCENARIO_FAILURE,
      { variant: "error" }
    );
    this.updateScenarioActiveStatus(
      this.props.userData.userProfile.scenarioID
    );
  }

  updateScenarioActiveStatus = (activeScenarioId) => {
    const updatedScenarios = this.state.scenariosData.map((scenario) => ({
      ...scenario,
      isActive: scenario.scenarioId === activeScenarioId
    }));
    this.setState({
      scenariosData: updatedScenarios,
    });
    this.gridApi.refreshCells({ columns: ["isActive"], force: true })
  };

  updateDataInStore = async () => {
    if (isDispatchHeaderActive()) {
      this.updateAnchorPointDataInStore();
      this.updateTrailerDataInStore();
      this.updateWavesDataInStore();
    } else {
      Events.emit('RefreshIBStore', {
        showNotification: false,
        refreshAdditionalData: {
          fetchAnchorPoint: true,
          fetchTrailerData: true,
          fetchWavesData: true
        }
      });
    }
  };

  updateAnchorPointDataInStore = async () => {
    const { scenarioID, terminal } = this.props.userData.userProfile;
    const anchorPointData = await getAnchorPointData(scenarioID, terminal);
    this.props.CommonStore.updateAnchorPointsData(anchorPointData, terminal.code);
  };

  updateTrailerDataInStore = async () => {
    const { terminal } = this.props.userData.userProfile;
    const trailerData = await getTrailersData(terminal);
    this.props.CommonStore.updateTrailersData(trailerData);
  };

  updateWavesDataInStore = async () => {
    const { scenarioID } = this.props.userData.userProfile;
    const wavesData = await getWavesData(scenarioID);
    this.props.CommonStore.updateWavesData(wavesData);
  };

  copyScenarioData = (params, isDefaultScenario = false) => {
    this.copyScenarioInProgress = true;
    const { scenarioName } = params.data;
    if (isDefaultScenario) {
      this.setState({
        createDefaultScenarioInProgress: true,
      });
    }
    const requestParams = {
      scenarioId: this.currentScenarioId,
      newScenarioName: sanitizeInput(scenarioName),
      isDefault:isDefaultScenario,
    };
    postData("CopyScenario", requestParams).then((response) => {
      if (response && response.status >= 200 && response.status < 400) {
        if (this._isComponentMounted) {
          if (isDefaultScenario) {
            this.refreshScenarioNameAfterDefaultCopy = true;
          }
          this.setState({
            scenariosData: response.data,
            errorMessage: null,
            createDefaultScenarioInProgress: false,
          });
        }
        this.props.enqueueSnackbar(`${scenarioName} created`, {
          variant: "success",
        });
        if (isDefaultScenario) {
          Events.emit(
            "RefreshUserInfo",
            this.props.userData.userProfile.terminal.id
          );
        }
      } else {
        this.handleCopyScenarioError(params, response.status);
      }
      this.copyScenarioInProgress = false;
    });
    this.currentScenarioId = null;
  };

  handleCopyScenarioError = (params, statusCode) => {
    if (statusCode !== 403) {
      this.props.enqueueSnackbar(`Error creating scenario ${params.data.scenarioName}`, {
        variant: "error",
      });
    }
    if (params?.colDef?.field) {
      params.data[params.colDef.field] = params.oldValue;
    }
    this.gridApi.refreshCells();
    if (this._isComponentMounted) {
      this.setState({
        createDefaultScenarioInProgress: false,
      });
    }
  }

  updateScenarioData = (params) => {
    const { scenarioId, scenarioName, scenarioDescription } = params.data;
    const requestParams = {
      scenarioId,
      scenarioName: sanitizeInput(scenarioName),
      scenarioDescription:sanitizeInput(scenarioDescription),
    };
    postData("EditScenario", requestParams).then(
      (response) => {
        if (Math.trunc(response.status / 100) === 2) {
          this.props.enqueueSnackbar(`${scenarioName} details updated`, {
            variant: "success",
          });
          Events.emit(
            "RefreshUserInfo",
            this.props.userData.userProfile.terminal.id,
            null,
            false
          );
          return;
        }
        this.handleError({params, scenarioName, statusCode: response.status});
      },
      () => {
        this.handleError({params, scenarioName});
      }
    );
  };

  handleError = ({params, scenarioName, statusCode}) => {
    params.data[params.colDef.field] = params.oldValue;
    this.gridApi.refreshCells();
    if (statusCode === 403) {
      return;
    }
    this.props.enqueueSnackbar(`Error updating ${scenarioName} details`, {
      variant: "error",
    });
  }

  copyScenario = () => {
    if (this.state.isEditing) return;
    if (this.gridApi.getSelectedRows().length !== 1) {
      this.props.enqueueSnackbar(MESSAGES.SCENARIO.COPY_NO_SCENARIO_SELECTED, {
        variant: "info",
      });
    } else {
      this.currentScenarioId =
        this.gridApi.getSelectedRows().length &&
        this.gridApi.getSelectedRows()[0].scenarioId;
      if (this.currentScenarioId) {
        const emptyRows = this.gridApi
          .getRenderedNodes()
          .filter((row) => row.data.scenarioId === null)
          .map((row) => row.data);
        if (emptyRows && emptyRows.length > 0) {
          this.gridApi.applyTransaction({
            remove: emptyRows,
          });
        }
        this.gridApi.applyTransactionAsync({
          add: [
            {
              scenarioId: null,
              scenarioName: '',
              scenarioDescription: `Copy of Scenario ${this.currentScenarioId}`,
              isDefault: 0,
              disabled: true
            }
          ]
        }, () => {
          const lastRowIndex = this.state.scenariosData.length;
          this.gridApi.forEachNode((node) => {
            if (lastRowIndex === node.rowIndex) {
              node.setSelected(true);
              this.gridApi.startEditingCell({
                rowIndex: node.rowIndex,
                colKey: 'scenarioName'
              });
            }
          });
        });
        this.startcopy = true;
      } else {
        if (!this.copyScenarioInProgress) {
          this.gridApi.applyTransaction({
            remove: [this.gridApi.getSelectedRows()[0]],
          });
        }
        this.props.enqueueSnackbar(
          MESSAGES.SCENARIO.COPY_NO_EXISTING_SCENARIO_SELECTED,
          { variant: "info" }
        );
      }
    }
  };

  // onCellClicked = (params) => {
  //     const lastRow = this.gridApi.getDisplayedRowAtIndex(this.gridApi.getLastDisplayedRow());
  //     const selectedRow = this.gridApi.getSelectedRows() && this.gridApi.getSelectedRows()[0];
  //     if(lastRow && !lastRow.data.scenarioId && selectedRow && selectedRow.scenarioId){
  //         if(!this.copyScenarioInProgress){
  //             this.gridApi.applyTransaction({
  //                 remove:[lastRow.data]
  //             });
  //         }
  //     }
  // }

  handleCreateNewScenario = () => {
    this.setState({
      showCreateNewScenarioWarning : true
    })
  }

  cancelNewDefaultScenarioCreation = () => {
    this.setState({
      showCreateNewScenarioWarning: false,
    });
  }

  createNewDefaultScenario = () => {
    this.setState({
      showCreateNewScenarioWarning: false
    })
    const masterScenarioId = this.state.scenariosData.find(
      (scenario) => scenario.isMaster
    )?.scenarioId ?? null;
    this.currentScenarioId = masterScenarioId;
    const defaultScenarioName = `Default_${format(
      getUTCDate(this.state.date),
      this.PLAN_DATE_FORMAT
    )}`;
    let defaultScenarioCount = this.state.scenariosData.filter((scenario) =>
      scenario.scenarioName.includes(defaultScenarioName)
    ).length;
    while (
      this.state.scenariosData.find(
        (scenario) =>
          scenario.scenarioName ===
          `${defaultScenarioName}${
            defaultScenarioCount ? `_${defaultScenarioCount}` : ""
          }`
      )
    ) {
      defaultScenarioCount++;
    }
    const params = {
      data: {
        scenarioId: null,
        scenarioName: `${defaultScenarioName}${
          defaultScenarioCount ? `_${defaultScenarioCount}` : ""
        }`,
      },
    };
    this.copyScenarioData(params, true);
  };

  render() {
    const { classes, userData } = this.props;
    const userProfile = userData.userProfile;
    const planDate = formatDateYear({ value: this.state.date });
    const enableCreateDefaultScenario = this.state.scenariosData?.length && userProfile.isAdmin;   
    return (
      <Fragment>
        <header
          className={classes.moduleHeader}
          data-automationid="scenarioExplorer-title"
        >
          Scenario Explorer
        </header>
        <div className={classes.moduleContainer}>
          <div className={classes.moduleMessage}>
            Admin users can alter active scenario and also conduct What-If
            analysis with different parameter values
          </div>

          <div className={classes.dateContainer}>
            <div className={classes.terminalText}> Date : </div>
            <span className={classes.actionEditor}>
              <DateTimeEditor
                dateType={appConstant.DATETYPES.DATEYEAR}
                onChange={this.onDateChanged}
                value={planDate}
              />
            </span>
          </div>
          <div
            className={`${classes.gridContainer} ${classes.scenarioGridContainer}`}
          >
            <div className={classes.gridHead}>
              <span className={classes.gridActionIcons}>
                <Tooltip title="Copy Scenario" delay={500}>
                  <div className={classes.gridHeaderActionContainer}>
                    <span className={classes.copyLabel}>Copy</span>
                    <i
                      className={`icon-copy ${classes.copyIcon} ${
                        this.state.isEditing ? classes.actionIconDisabled : ""
                      }`}
                      onClick={this.copyScenario}
                    />
                  </div>
                </Tooltip>
              </span>
            </div>

            <div className={`${classes.dataGrid}`}>
              {!this.state.errorMessage ? (
                <DataGrid
                  defaultColDef={this.gridConfig.config.defaultColDef}
                  columnDefs={this.gridConfig.config.columns}
                  config={this.gridConfig.config}
                  rowData={this.state.scenariosData}
                  onGridReady={this.onGridReady}
                  onRowDataUpdated={this.onScenariosRowDataUpdated}
                  onCellValueChanged={this.onCellValueChanged}
                  suppressHorizontalScroll
                  getRowId={(params) => params.data.scenarioId}
                />
              ) : (
                <div className={classes.errorMessageContainer}>
                  <div className={classes.errorMessage}>
                    {this.state.errorMessage}
                  </div>
                </div>
              )}
            </div>
          </div>
          <Tooltip title={enableCreateDefaultScenario? "": "No scenarios available for the selected date"}>
            <div className={classes.createScenarioButton}>
              <Button
                color="primary"
                variant="contained"
                size="small"
                classes={{
                  root: `${classes.actionButton} ${classes.scenarioActionButton}`,
                  disabled: classes.actionDisabled,
                }}
                disabled={!enableCreateDefaultScenario}
                onClick={this.handleCreateNewScenario}
                data-automationid="createNewDefaultScenario"
              >
                {this.state.createDefaultScenarioInProgress ? (
                  <CircularProgress
                    size={16}
                    className={classes.actionInProgress}
                  />
                ) : (
                  LABELS.CREATE_NEW_DEFAULT_SCENARIO
                )}
              </Button>
            </div>
          </Tooltip>
          {this.state.showCreateNewScenarioWarning && (
            <CustomConfirmationDialog
              title={LABELS.CREATE_NEW_DEFAULT_SCENARIO}
              cancelButtonLabel="Cancel"
              confirmButtonLabel="Confirm"
              onConfirm={this.createNewDefaultScenario}
              onCancel={this.cancelNewDefaultScenarioCreation}
            >
              <div>{WARNING_MESSAGE}</div>
            </CustomConfirmationDialog>
          )}
        </div>
      </Fragment>
    );
  }
}
export default compose(
  withStyles(styles),
  withUserProfile,
  withSnackbar
)(ScenarioExplorer);
